// « 💬 Ton avis » (D-103) : les retours du club pilote, sur chaque écran.
// Le bouton se pose à côté du « ? » de l'en-tête (src/guide/screen-help.ts), sur sa ligne : il ne couvre ni le
// contenu ni les onglets. Seulement pour les comptes d'un club dont la formule est « pilote » (support_avis_open,
// la base décide). La feuille : la page détectée (« Tu es sur : … », avec l'équipe), ce qui se passe (ça ne
// marche pas, je n'ai pas compris, il manque quelque chose, une idée), le message, « Ça servirait à d'autres
// clubs ? », une capture facultative montrée avant l'envoi. L'avis part au support (D-082) : l'auteur le suit
// dans « Mes messages », prévenu quand il est traité ; l'équipe Arceau le lit dans la boîte du support et la
// tâche SAV le traite. Tutoiement ; vouvoiement pour un compte partenaire (D-074).
import './feedback.css';
import {
  AVIS_TOPICS, BODY_MAX, OTHERS, OTHERS_LABEL, SHOT_MAX, TOPIC_KIND, TOPIC_LABEL, avisPageLabel, browserLabel, pageOf, topicHint,
  type AvisOthers, type AvisTopic,
} from '../help/support';
import { SCREENS, screenKey } from '../help/screens';
import { avisOpen, avisOpenKnown, sendAvis } from '../lib/feedback';
import { appVersion } from '../lib/support';
import { eventById, playById } from '../lib/api';
import { personaOf, workTeams, type Ctx } from './context';
import { $, $$, html, mount, openSheet, toast } from './dom';
import { casquetteOf, compressShot } from './help';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Écrans sans le bouton : la boîte du support, la console, et l'écran où l'on écrit déjà au support. */
const NOT_HERE = (hash: string) => /^#\/(support|console)(\/|$)/.test(hash) || hash.startsWith('#/aide/ecrire');

const vousOf = (c: Ctx) => personaOf(c) === 'partenaire';
const clubOf = (c: Ctx) => (c.club && UUID.test(c.club.id) && c.hat !== 'console' ? c.club.id : null);

/** Pose « 💬 Ton avis » dans l'en-tête qui porte déjà le « ? » (classe gd-has-q). La réponse de la base
 *  arrive une fois par club : la première fois, le bouton paraît dès qu'elle est là. */
export function placeAvis(head: HTMLElement, c: Ctx, hash: string): void {
  const club = clubOf(c);
  if (!club || NOT_HERE(hash) || head.querySelector('.gd-avis')) return;
  const put = () => {
    if (!head.isConnected || location.hash !== hash || head.querySelector('.gd-avis')) return;
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'gd-avis';
    b.dataset.guide = 'avis';
    b.innerHTML = `<span aria-hidden="true">💬</span><span>${vousOf(c) ? 'Votre avis' : 'Ton avis'}</span>`;
    b.setAttribute('aria-label', vousOf(c) ? 'Votre avis sur cet écran' : 'Ton avis sur cet écran');
    b.addEventListener('click', () => openAvis(c, hash));
    head.classList.add('gd-has-avis');
    head.append(b);
  };
  const known = avisOpenKnown(club);
  if (known === true) put();
  else if (known === undefined) void avisOpen(club).then(on => { if (on) put(); });
}

/** L'équipe de la page : dans l'adresse (équipe, séance, match, play), sinon la seule équipe de l'écran. */
async function teamOfPage(c: Ctx, hash: string): Promise<string | null> {
  const seg = hash.replace(/^#\/?/, '').split('/');
  const named = (id: string | null | undefined) => (id ? c.full.teams.find(t => t.id === id)?.name ?? null : null);
  for (const s of seg) { const n = named(s); if (n) return n; }
  const id = seg[1] && UUID.test(seg[1]) ? seg[1] : null;
  if (id && ['e', 'seance', 'stats', 'repartir'].includes(seg[0] ?? '')) { const n = named((await eventById(id).catch(() => null))?.team_id); if (n) return n; }
  if (id && seg[0] === 'play') { const n = named((await playById(id).catch(() => null))?.team_id); if (n) return n; }
  if (SCREENS[screenKey(hash)]?.team) { const w = workTeams(c); if (w.length === 1) return w[0]!.name; }
  return null;
}

const ko = (n: number) => `${Math.max(1, Math.round(n * 0.75 / 1024)).toLocaleString('fr-FR')} Ko`;

export function openAvis(c: Ctx, hash: string): void {
  const club = clubOf(c);
  if (!club) return;
  const vous = vousOf(c);
  const tv = (tu: string, vo: string) => (vous ? vo : tu);
  const key = screenKey(hash);
  const screen = key.startsWith('rt/') ? SCREENS[key]?.label ?? null : null;
  let topic: AvisTopic | null = null;
  let others: AvisOthers | null = null;
  let shot: string | null = null;
  let team: string | null = null;
  const page = pageOf(hash) || '#/';
  const label = () => avisPageLabel(page, team, screen);
  openSheet(vous ? 'Votre avis' : 'Ton avis', html`
    <form class="av" id="f-avis" novalidate>
      <p class="av-where">${tv('Tu es sur', 'Vous êtes sur')} : <strong id="av-page">${label()}</strong></p>
      <div class="av-topics" role="group" aria-label="Ce qui se passe">${AVIS_TOPICS.map(t => html`
        <button type="button" class="av-topic av-${t}" data-topic="${t}" aria-pressed="false">${TOPIC_LABEL[t]}</button>`)}</div>
      <label class="field"><span>${tv('Ton message', 'Votre message')}</span>
        <textarea name="body" id="av-body" rows="4" maxlength="${BODY_MAX}" required placeholder="${topicHint(null, vous)}"></textarea></label>
      <div class="field"><span>Ça servirait à d’autres clubs ?</span>
        <div class="seg av-others" role="group" aria-label="Ça servirait à d’autres clubs ?">${OTHERS.map(o => html`
          <button type="button" data-others="${o}" aria-pressed="false">${OTHERS_LABEL[o]}</button>`)}</div></div>
      <div class="field"><span>Capture d’écran (facultatif)</span><div id="av-shot"></div></div>
      <p class="av-ctx muted small">${tv('Joint à ton avis', 'Joint à votre avis')} : la page, la version de l’app, ${browserLabel(navigator.userAgent)}, ${tv('ta casquette', 'votre compte')}.</p>
      <p class="form-msg" id="av-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit" id="av-send">Envoyer</button>
      <p class="hint">L’équipe qui fait Arceau (pas le club) lit chaque avis. ${tv('Tu suis la suite dans « Mes messages », avec une notification quand c’est traité.', 'Vous suivez la suite dans « Mes messages », avec une notification quand c’est traité.')}</p>
    </form>`, (sheet, close) => {
    void teamOfPage(c, hash).then(n => { team = n; const el = $(sheet, '#av-page'); if (el) el.textContent = label(); });
    const body = $<HTMLTextAreaElement>(sheet, '#av-body')!;
    for (const b of $$<HTMLButtonElement>(sheet, '[data-topic]')) b.addEventListener('click', () => {
      topic = b.dataset.topic as AvisTopic;
      $$(sheet, '[data-topic]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      body.placeholder = topicHint(topic, vous);
      $(sheet, '#av-msg')!.textContent = '';
    });
    for (const b of $$<HTMLButtonElement>(sheet, '[data-others]')) b.addEventListener('click', () => {
      others = others === b.dataset.others ? null : b.dataset.others as AvisOthers;
      $$(sheet, '[data-others]').forEach(x => x.setAttribute('aria-pressed', String(x.dataset.others === others)));
    });
    // La capture : prise avec le téléphone, ajoutée ici, montrée avant l'envoi (elle peut montrer des prénoms).
    const shotEl = $(sheet, '#av-shot')!;
    const drawShot = () => {
      mount(shotEl, shot ? html`
        <div class="av-shot"><img src="${shot}" alt="Capture jointe à ${tv('ton', 'votre')} avis">
          <div class="av-shot-t"><p class="av-warn">${tv('Elle peut montrer des prénoms : regarde-la avant d’envoyer.', 'Elle peut montrer des prénoms : regardez-la avant d’envoyer.')}</p>
            <span class="muted small">${ko(shot.length)} · seule l’équipe Arceau la voit</span>
            <button type="button" class="btn btn-link btn-sm" id="av-shot-x">Retirer</button></div></div>`
        : html`<div class="av-add"><label class="btn btn-ghost btn-sm hp-file">Ajouter une capture<input type="file" id="av-file" accept="image/*" hidden></label>
          <small class="hint">${tv('Fais une capture avec ton téléphone, puis ajoute-la ici.', 'Faites une capture avec votre téléphone, puis ajoutez-la ici.')}</small></div>`);
      $(shotEl, '#av-shot-x')?.addEventListener('click', () => { shot = null; drawShot(); });
      $<HTMLInputElement>(shotEl, '#av-file')?.addEventListener('change', async (e) => {
        const f = (e.target as HTMLInputElement).files?.[0];
        if (!f) return;
        if (!f.type.startsWith('image/')) { toast('Une image seulement (capture d’écran, photo).', 'ko'); return; }
        try { const s = await compressShot(f); if (s.length > SHOT_MAX) throw new Error('trop lourde'); shot = s; drawShot(); }
        catch { toast('Image illisible ou trop lourde.', 'ko'); }
      });
    };
    drawShot();

    const form = $<HTMLFormElement>(sheet, '#f-avis')!;
    const msg = $(sheet, '#av-msg')!;
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const text = body.value.trim();
      if (!topic) { msg.textContent = tv('Choisis ce qui se passe : ça ne marche pas, pas compris, il manque quelque chose ou une idée.', 'Choisissez ce qui se passe : ça ne marche pas, pas compris, il manque quelque chose ou une idée.'); return; }
      if (!text) { msg.textContent = tv('Écris quelques mots.', 'Écrivez quelques mots.'); body.focus(); return; }
      msg.textContent = '';
      const send = $<HTMLButtonElement>(sheet, '#av-send')!;
      send.disabled = true;
      try {
        await sendAvis({ club_id: club, topic, kind: TOPIC_KIND[topic], body: text, for_other_clubs: others, page, page_label: label(),
          app_version: await appVersion(), user_agent: navigator.userAgent.slice(0, 400), casquette: casquetteOf(c).slice(0, 80), screenshot: shot });
      } catch (err) {
        send.disabled = false;
        msg.textContent = (err as { code?: string }).code === '54000'
          ? tv('10 messages par jour au plus : réessaie demain.', '10 messages par jour au plus : réessayez demain.')
          : 'Envoi impossible pour le moment.';
        return;
      }
      mount(form, html`
        <div class="av-done">
          <span class="av-done-ico" aria-hidden="true">✓</span>
          <p class="empty-title">Merci !</p>
          <p>${tv('Ton avis est arrivé à l’équipe Arceau. Tu suis la suite dans « Mes messages » : une notification te prévient quand c’est traité.',
            'Votre avis est arrivé à l’équipe Arceau. Vous suivez la suite dans « Mes messages » : une notification vous prévient quand c’est traité.')}</p>
          <div class="btn-row"><a class="btn btn-primary" href="#/aide/messages" id="av-mine">Mes messages</a><button type="button" class="btn btn-ghost" id="av-close">Fermer</button></div>
        </div>`);
      $(sheet, '#av-close')?.addEventListener('click', close);
      $(sheet, '#av-mine')?.addEventListener('click', close);
    });
  });
}
